import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Alert, AlertTitle, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSelector } from "react-redux";
import { Form } from "../../Form";
import FormSelect from "../../Form/FormSelect";
import FormAutocomplete from "../../Form/FormAutocomplete";
import FormSubmitButton from "../../Form/FormSubmitButton";
import Checkbox from "../../Checkbox";
import { AutocompleteOption } from "../../Autocomplete";
import { sessionsService } from "../../../services/sessionsService";
import {
  treatmentPlanService,
  TreatmentPlanListItem,
  TreatmentPlanResponse,
} from "../../../services/treatmentPlanService";
import { TreatmentPlanStatus } from "../../../types/treatmentPlan";
import { selectUser } from "../../../store/selectors/authSelectors";
import {
  StartSessionModalSubtitle,
  SessionTypeLabel,
  StartSessionModalActions,
  SessionTypeToggleContainer,
  SessionTypeOption,
} from "./StartSessionModal.styled";
import { useModal } from "../ModalProvider";
import { patientsService } from "../../../services/patientsService";
import { FormDataType, useFormContext } from "../../Form/FormContext";
import { ValidationResult } from "../../../hooks/useFormValidation";

// Helper component to reset specific form fields when dependencies change
interface FormFieldResetterProps {
  fields: string[];
  dependencies: unknown[];
}

const FormFieldResetter: React.FC<FormFieldResetterProps> = ({
  fields,
  dependencies,
}) => {
  const { updateField } = useFormContext();
  const isFirstRender = React.useRef(true);

  useEffect(() => {
    // Skip first render to avoid clearing on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Reset specified fields when dependencies change
    fields.forEach((field) => {
      updateField(field, "");
    });
  }, dependencies); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export interface SessionData {
  sessionId: number;
  patientId: string;
  sessionType: "treatment" | "consult";
  treatmentPlanId?: string;
  treatmentAppointmentId?: string;
}

interface StartSessionModalContentProps {
  onProceed: (sessionData: SessionData) => void;
  onSubmitRef?: (fn: () => void) => void;
}

// Using AutocompleteOption from Autocomplete component

interface TreatmentAppointmentOption {
  id: number;
  title: string;
  sortOrder: number;
}

const StartSessionModalContent: React.FC<StartSessionModalContentProps> = ({
  onProceed,
}) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const user = useSelector(selectUser);
  const [sessionType, setSessionType] = useState<"treatment" | "consult">(
    "consult",
  );
  const [patients, setPatients] = useState<AutocompleteOption[]>([]);
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlanListItem[]>(
    [],
  );
  const [treatmentAppointments, setTreatmentAppointments] = useState<
    TreatmentAppointmentOption[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [selectedTreatmentPlanId, setSelectedTreatmentPlanId] = useState<
    number | null
  >(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    number | null
  >(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);

  // Treatment session is only available if patient has treatment plans with In Progress status
  const hasInProgressPlans = treatmentPlans.length > 0;
  const isTreatmentDisabled = selectedPatientId ? !hasInProgressPlans : true;

  const fetchPatients = useCallback(async () => {
    if (!user?.id) {
      console.error("User ID not available");
      return;
    }

    setLoading(true);
    try {
      const response = await patientsService.getPatients({
        providerId: [user.id],
        limit: 100,
      });
      const patientOptions: AutocompleteOption[] = response.data.map((p) => ({
        value: p.id,
        label: `${p.firstName} ${p.lastName}`,
      }));
      setPatients(patientOptions);
    } catch (error) {
      console.error("Error fetching patients:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);


  // Fetch treatment plans with In Progress status for the selected patient
  const fetchTreatmentPlans = useCallback(async (patientId: number) => {
    try {
      const response = await treatmentPlanService.getList({
        patientId: [patientId],
        status: [TreatmentPlanStatus.InProgress], // Only In Progress plans
        limit: 100,
      });
      setTreatmentPlans(response.data);
    } catch (error) {
      console.error("Error fetching treatment plans:", error);
      setTreatmentPlans([]);
    }
  }, []);

  // Fetch treatment appointments from the selected treatment plan
  const fetchTreatmentAppointments = useCallback(
    async (treatmentPlanId: number) => {
      setLoadingAppointments(true);
      try {
        const plan: TreatmentPlanResponse =
          await treatmentPlanService.getById(treatmentPlanId);

        // Extract appointments
        const appointments: TreatmentAppointmentOption[] = plan.appointments.map(
          (appointment) => ({
            id: appointment.id,
            title: `Appointment #${appointment.sortOrder}`,
            sortOrder: appointment.sortOrder,
          }),
        );

        // Sort appointments by sortOrder
        appointments.sort((a, b) => a.sortOrder - b.sortOrder);

        setTreatmentAppointments(appointments);
      } catch (error) {
        console.error("Error fetching treatment appointments:", error);
        setTreatmentAppointments([]);
      } finally {
        setLoadingAppointments(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const validationRules = {
    patient: {
      required: true,
      message: "Please select a patient",
    },
    ...(sessionType === "treatment" && {
      treatmentPlan: {
        required: true,
        message: "Please select a treatment plan",
      },
      treatmentAppointment: {
        required: true,
        message: "Please select an appointment",
      },
    }),
  };

  const handleSubmit = async (
    formData: FormDataType,
    validationResult: ValidationResult,
  ) => {
    if (!validationResult.isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (!user?.id) {
        console.error("User ID not found");
        return;
      }

      // Create session via API
      const createSessionResponse = await sessionsService.createSession({
        patientId: Number(formData.patient),
        treatmentPlanId:
          sessionType === "treatment"
            ? Number(formData.treatmentPlan)
            : undefined,
        treatmentPlanAppointmentId:
          sessionType === "treatment" && selectedAppointmentId
            ? selectedAppointmentId
            : undefined,
        accountId: user.id,
        type: sessionType === "treatment" ? 2 : 1, // 1 = consult, 2 = treatment
      });

      console.log("Session created:", createSessionResponse);

      // Start the session
      await sessionsService.startSession(createSessionResponse.id);
      console.log("Session started:", createSessionResponse.id);

      const sessionData: SessionData = {
        sessionId: createSessionResponse.id,
        patientId: String(formData.patient),
        sessionType: sessionType,
        ...(sessionType === "treatment" &&
          selectedAppointmentId && {
            treatmentPlanId: String(formData.treatmentPlan),
            treatmentAppointmentId: String(selectedAppointmentId),
          }),
      };

      onProceed(sessionData);
    } catch (error) {
      console.error("Error creating/starting session:", error);
      // TODO: Show error message to user
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePatientChange = (value: AutocompleteOption | null) => {
    setHasChanges(true);
    setTreatmentPlans([]);
    setTreatmentAppointments([]);
    setSelectedTreatmentPlanId(null);
    setSelectedAppointmentId(null);
    // Reset session type to default when patient changes
    setSessionType("consult");

    if (!value) {
      setSelectedPatientId("");
      return;
    }
    const patientId = String(value.value);
    setSelectedPatientId(patientId);

    // Fetch treatment plans with In Progress status for this patient
    fetchTreatmentPlans(Number(value.value));
  };

  const handleTreatmentPlanChange = (
    value: string | number | (string | number)[] | boolean | null | undefined,
  ) => {
    setHasChanges(true);
    setTreatmentAppointments([]);
    setSelectedAppointmentId(null);

    if (!value || typeof value === "boolean") {
      setSelectedTreatmentPlanId(null);
      return;
    }

    const planId = Number(value);
    setSelectedTreatmentPlanId(planId);

    if (planId) {
      fetchTreatmentAppointments(planId);
    }
  };

  const handleAppointmentChange = (
    value: string | number | (string | number)[] | boolean | null | undefined,
  ) => {
    setHasChanges(true);

    if (!value || typeof value === "boolean") {
      setSelectedAppointmentId(null);
      return;
    }

    const appointmentId = Number(value);
    setSelectedAppointmentId(appointmentId);
  };

  const handleSessionTypeChange = (type: "treatment" | "consult") => {
    setHasChanges(true);
    setSessionType(type);
    // Treatment plans are already fetched when patient is selected
    // Clear appointments when switching session type
    if (type === "consult") {
      setTreatmentAppointments([]);
      setSelectedTreatmentPlanId(null);
      setSelectedAppointmentId(null);
    }
  };

  const handleCancel = () => {
    if (hasChanges && !showCancelWarning) {
      setShowCancelWarning(true);
      return;
    }
    closeModal("start-session-modal");
  };

  return (
    <Box width="100%">
      <StartSessionModalSubtitle>
        {t("startSessionModal.subtitle")}
      </StartSessionModalSubtitle>

      <Form validationRules={validationRules} onSubmit={handleSubmit}>
        {/* Reset treatment fields when session type or patient changes */}
        <FormFieldResetter
          fields={["treatmentPlan", "treatmentAppointment"]}
          dependencies={[sessionType, selectedPatientId]}
        />

        <FormAutocomplete
          name="patient"
          label={t("startSessionModal.selectPatient")}
          placeholder={t("startSessionModal.selectPatientPlaceholder")}
          options={patients}
          required
          loading={loading}
          onAutocompleteChange={handlePatientChange}
        />

        <Box>
          <SessionTypeLabel>
            {t("startSessionModal.sessionType")}
          </SessionTypeLabel>
          <SessionTypeToggleContainer>
            <SessionTypeOption
              selected={sessionType === "treatment"}
              onClick={() => {
                if (!selectedPatientId || isTreatmentDisabled) return;
                handleSessionTypeChange("treatment");
              }}
              sx={{
                opacity: !selectedPatientId || isTreatmentDisabled ? 0.5 : 1,
                cursor:
                  !selectedPatientId || isTreatmentDisabled
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <Box>{t("startSessionModal.treatmentSession")}</Box>
              <Checkbox
                checked={sessionType === "treatment"}
                disabled={!selectedPatientId || isTreatmentDisabled}
              />
            </SessionTypeOption>
            <SessionTypeOption
              selected={sessionType === "consult"}
              onClick={() => {
                if (!selectedPatientId) return;
                handleSessionTypeChange("consult");
              }}
              sx={{
                opacity: !selectedPatientId ? 0.5 : 1,
                cursor: !selectedPatientId ? "not-allowed" : "pointer",
              }}
            >
              <Box>{t("startSessionModal.consultSession")}</Box>
              <Checkbox
                checked={sessionType === "consult"}
                disabled={!selectedPatientId}
              />
            </SessionTypeOption>
          </SessionTypeToggleContainer>
        </Box>

        {sessionType === "treatment" && selectedPatientId && (
          <Box sx={{ display: "flex", gap: "8px", flexDirection: "column" }}>
            <FormSelect
              name="treatmentPlan"
              label={t("startSessionModal.selectTreatmentPlan")}
              placeholder={t(
                "startSessionModal.selectTreatmentPlanPlaceholder",
              )}
              options={treatmentPlans.map((plan) => ({
                value: plan.id,
                label: plan.name,
              }))}
              required
              disabled={!selectedPatientId || treatmentPlans.length === 0}
              onSelectChange={handleTreatmentPlanChange}
            />

            {selectedTreatmentPlanId &&
              !loadingAppointments &&
              treatmentAppointments.length > 0 && (
                <FormSelect
                  name="treatmentAppointment"
                  label={t("startSessionModal.selectAppointment")}
                  placeholder={t(
                    "startSessionModal.selectAppointmentPlaceholder",
                  )}
                  options={treatmentAppointments.map((apt) => ({
                    value: apt.id,
                    label: apt.title,
                  }))}
                  required
                  onSelectChange={handleAppointmentChange}
                />
              )}

            {loadingAppointments && (
              <Box sx={{ color: "#6B7A94", fontSize: "14px", padding: "8px 0" }}>
                {t("startSessionModal.loadingAppointments")}
              </Box>
            )}
          </Box>
        )}

        {showCancelWarning && (
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon />}
            sx={{
              marginTop: "16px",
              marginBottom: "16px",
              backgroundColor: "rgba(211, 47, 47, 0.08)",
              border: "none",
              "& .MuiAlert-icon": {
                color: "#D32F2F",
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: "16px" }}>
              Are you sure you want to cancel the session?
            </AlertTitle>
            <Box sx={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.87)" }}>
              Unsaved changes will be lost.
            </Box>
          </Alert>
        )}

        <StartSessionModalActions>
          <Button variant="outlined" onClick={handleCancel} type="button" disabled={isSubmitting}>
            {t("startSessionModal.cancel")}
          </Button>
          <FormSubmitButton type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? t("common.uploading") : t("startSessionModal.proceed")}
            {isSubmitting && (
              <CircularProgress size={20} sx={{ marginLeft: 1 }} color="inherit" />
            )}
          </FormSubmitButton>
        </StartSessionModalActions>
      </Form>
    </Box>
  );
};

export default StartSessionModalContent;
